import { type DateRange } from "react-day-picker"
import { isSameDay } from "date-fns"
import { formatDate } from "@utils/formatters"
import type {
  TDateRangeISO,
  TDateRangePreset,
  TSelectDateRangeProps,
} from "./types"
import { defaultDateRangePresets } from "./presets"
import { useMemo, useState } from "react"

/**
 * Convert DateRange (Date objects) to DateRangeISO (ISO strings)
 * Use this when storing or transmitting date ranges
 *
 * @param range - DateRange with Date objects
 * @returns DateRangeISO with ISO strings, or undefined if range is invalid
 */
export const dateRangeToISO = (
  range: DateRange | undefined,
): TDateRangeISO | undefined => {
  if (!range?.from || !range?.to) return undefined

  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  }
}

/**
 * Convert DateRangeISO (ISO strings) to DateRange (Date objects)
 * Use this when displaying or working with date ranges in UI
 *
 * @param iso - DateRangeISO with ISO strings
 * @returns DateRange with Date objects, or undefined if iso is invalid
 */
export const isoToDateRange = (
  iso: TDateRangeISO | undefined,
): DateRange | undefined => {
  if (!iso?.from || !iso?.to) return undefined

  return {
    from: new Date(iso.from),
    to: new Date(iso.to),
  }
}

/**
 * Compare two date ranges to determine if they represent the same period.
 * Handles edge cases like undefined dates and uses day-level comparison (ignores time).
 * Used to identify which preset is currently active in the date range selector.
 *
 * @param range1 - First date range to compare
 * @param range2 - Second date range to compare
 * @returns true if both ranges represent the same period, false otherwise
 */
export const areDateRangesEqual = (
  range1: DateRange,
  range2: DateRange,
): boolean => {
  const from1 = range1.from
  const to1 = range1.to
  const from2 = range2.from
  const to2 = range2.to
  const range1Empty = !from1 && !to1
  const range2Empty = !from2 && !to2

  if (range1Empty || range2Empty) return range1Empty === range2Empty

  const fromEqual = from1 && from2 ? isSameDay(from1, from2) : from1 === from2
  const toEqual = to1 && to2 ? isSameDay(to1, to2) : to1 === to2

  return fromEqual && toEqual
}

/**
 * Format a date range for display
 */
export const formatDateRange = (range: DateRange) => {
  if (range.from && range.to) {
    return `${formatDate(range.from)} - ${formatDate(range.to)}`
  }
  if (range.from) {
    return formatDate(range.from)
  }
  if (range.to) {
    return formatDate(range.to)
  }
  return null
}

/**
 * Custom hook to manage date range selection logic
 */
export const useSelectDateRange = ({
  value,
  onChange,
  presets = defaultDateRangePresets,
}: Pick<TSelectDateRangeProps, "value" | "onChange" | "presets">) => {
  const [internalRange, setInternalRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })

  const isControlled = value !== undefined
  const currentRange = isControlled ? value : internalRange

  const handleSelect = (range: DateRange) => {
    if (isControlled) {
      onChange?.(range)
    } else {
      setInternalRange(range)
      onChange?.(range)
    }
  }

  const handlePresetClick = (preset: TDateRangePreset) => {
    const presetRange = preset.value()
    handleSelect(presetRange)
  }

  const handleClear = () => {
    const allPreset = defaultDateRangePresets.find(
      (preset) => preset.default,
    )
    if (allPreset) handleSelect(allPreset.value())
  }

  const activePreset = useMemo((): TDateRangePreset | null => {
    for (const preset of presets) {
      if (areDateRangesEqual(currentRange, preset.value())) {
        return preset
      }
    }
    return null
  }, [currentRange, presets])

  const displayText = activePreset?.label || formatDateRange(currentRange)

  return {
    currentRange,
    handleSelect,
    handlePresetClick,
    handleClear,
    activePreset,
    displayText,
  }
}
